import { useState, useCallback } from "react"
import { router, Link } from "@inertiajs/react"
import { Star, ImageOff, Trash2, ImagePlus, Images } from "lucide-react"
import { Lightbox, type GalleryImage } from "@/components/ImageGallery"

type AdminControls = {
  setPrimaryBasePath: string   // "/admin/image_attachments"
  removeBasePath: string       // "/admin/image_attachments"
  deleteBasePath: string       // "/admin/image_attachments"
  addImageHref: string
  attachExistingHref: string
  returnTo: string
}

type Props = {
  images: GalleryImage[]
  adminControls?: AdminControls
}


export function PhotosTab({ images, adminControls }: Props) {
  const [ lightboxIndex, setLightboxIndex ] = useState<number | null>(null)
  const closeLightbox = useCallback(() => setLightboxIndex(null), [])

  function handleSetPrimary(ia: GalleryImage) {
    if (!adminControls) return
    router.patch(`${adminControls.setPrimaryBasePath}/${ia.id}/set_primary`, {
      return_to: adminControls.returnTo,
    })
  }

  function handleRemove(ia: GalleryImage) {
    if (!adminControls) return
    if (!confirm("Remove this image? The image record will be kept.")) return
    router.delete(`${adminControls.removeBasePath}/${ia.id}`, {
      data: { keep_image: "true", return_to: adminControls.returnTo },
    })
  }

  function handleDelete(ia: GalleryImage) {
    if (!adminControls) return
    if (!confirm("Delete this image permanently? This cannot be undone.")) return
    router.delete(`${adminControls.deleteBasePath}/${ia.id}`, {
      data: { keep_image: "false", return_to: adminControls.returnTo },
    })
  }

  if (images.length === 0) {
    return (
      <div className="py-8 text-center">
        <p className="text-sm text-stone-500 dark:text-stone-400 mb-4">No photos yet.</p>
        {adminControls && (
          <div className="flex items-center justify-center gap-2">
            <Link
              href={adminControls.addImageHref}
              className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-stone-700 dark:text-stone-300 bg-stone-100 dark:bg-stone-800 hover:bg-stone-200 dark:hover:bg-stone-700 rounded-lg transition-colors no-underline"
            >
              <ImagePlus className="w-4 h-4" />
              Upload photo
            </Link>
            <Link
              href={adminControls.attachExistingHref}
              className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-stone-700 dark:text-stone-300 bg-stone-100 dark:bg-stone-800 hover:bg-stone-200 dark:hover:bg-stone-700 rounded-lg transition-colors no-underline"
            >
              <Images className="w-4 h-4" />
              Attach existing
            </Link>
          </div>
        )}
      </div>
    )
  }

  return (
    <>
      {lightboxIndex !== null && (
        <Lightbox images={images} startIndex={lightboxIndex} onClose={closeLightbox} />
      )}

      <div className="space-y-4">
        {adminControls && (
          <div className="flex justify-end gap-2">
            <Link
              href={adminControls.attachExistingHref}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-stone-700 dark:text-stone-300 bg-stone-100 dark:bg-stone-800 hover:bg-stone-200 dark:hover:bg-stone-700 rounded-lg transition-colors no-underline"
            >
              <Images className="w-3.5 h-3.5" />
              Attach existing
            </Link>
            <Link
              href={adminControls.addImageHref}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-stone-700 dark:text-stone-300 bg-stone-100 dark:bg-stone-800 hover:bg-stone-200 dark:hover:bg-stone-700 rounded-lg transition-colors no-underline"
            >
              <ImagePlus className="w-3.5 h-3.5" />
              Upload photo
            </Link>
          </div>
        )}

        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
          {images.map((img, i) => (
            <div key={img.id} className="relative group/photo">
              <button
                className="w-full aspect-square rounded-xl overflow-hidden bg-stone-100 dark:bg-stone-800 block"
                onClick={() => setLightboxIndex(i)}
                aria-label={img.alt_text || `Photo ${i + 1}`}
              >
                {img.thumb_url ? (
                  <img
                    src={img.thumb_url}
                    alt={img.alt_text}
                    className="w-full h-full object-cover group-hover/photo:scale-105 transition-transform duration-300"
                  />
                ) : (
                  <div className="w-full h-full bg-stone-200 dark:bg-stone-700" />
                )}
              </button>

              {img.is_primary && (
                <div className="absolute top-1.5 left-1.5 flex items-center gap-1 px-1.5 py-0.5 bg-amber-500 rounded-md pointer-events-none">
                  <Star className="w-3 h-3 text-white fill-white" />
                  <span className="text-[10px] font-semibold text-white leading-none">Primary</span>
                </div>
              )}

              {adminControls && (
                <div className="absolute inset-x-0 bottom-0 flex items-end justify-center pb-2 gap-1 opacity-0 group-hover/photo:opacity-100 transition-opacity">
                  {!img.is_primary && (
                    <button
                      onClick={(e) => { e.stopPropagation(); handleSetPrimary(img) }}
                      className="flex items-center gap-1 px-1.5 py-1 text-[10px] font-medium bg-amber-500/90 text-white rounded-md hover:bg-amber-500 transition-colors"
                      title="Make primary"
                    >
                      <Star className="w-3 h-3" />
                      Primary
                    </button>
                  )}
                  <button
                    onClick={(e) => { e.stopPropagation(); handleRemove(img) }}
                    className="flex items-center gap-1 px-1.5 py-1 text-[10px] font-medium bg-white/90 text-stone-700 rounded-md hover:bg-white transition-colors"
                    title="Remove"
                  >
                    <ImageOff className="w-3 h-3" />
                  </button>
                  <button
                    onClick={(e) => { e.stopPropagation(); handleDelete(img) }}
                    className="flex items-center gap-1 px-1.5 py-1 text-[10px] font-medium bg-red-600/90 text-white rounded-md hover:bg-red-600 transition-colors"
                    title="Delete permanently"
                  >
                    <Trash2 className="w-3 h-3" />
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </>
  )
}
